import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
} from "@mui/material";
import { Link, useNavigate, useLocation } from "react-router-dom";

import { CartContext } from "../../context/CartContext";
import api from "../../api/axios";
import { AuthContext } from '../../context/AuthContext';

// Import subcomponents
import SearchBox from "./SearchBox";
import UserMenu from "./UserMenu";
import HeaderIcons from "./HeaderIcons";

const Header = () => {
    const { user,vendorProfile,agentProfile,loading,logout, updateUser } = useContext(AuthContext);
// console.log("Header agentProfile:", agentProfile); // Debug log for user context
  const navigate = useNavigate();
  const location = useLocation();
  const { cartCount } = useContext(CartContext);

  const [query, setQuery] = useState("");
  // const [user, setUser] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [vendorLogo, setVendorLogo] = useState(null);

  const [suggestions, setSuggestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  useEffect(() => {
    const saved = localStorage.getItem("user");
    const parsed = saved ? JSON.parse(saved) : null;
    // setUser(parsed);
    // Reset menu state when component mounts
    setMenuOpen(false);
    setAnchorEl(null);
  }, [location.pathname, user?.role]);

  const role = user?.role || null;
  const avatarSrc = vendorLogo || user?.avatar;
  // Remove debug log in final version

  const dashboardRoute = role
    ? role === "admin"
      ? "/admin"
      : role === "vendor"
      ? "/vendor/dashboard"
      : role === "agent"
      ? "/agent/dashboard"
      : "/customer/dashboard"
    : "/login";

  // Fetch profile image for vendor AND agent (and not in registration flow)
  useEffect(() => {
    if (role !== "vendor" && role !== "agent" && role !== "customer" && role !== "admin") {
      setVendorLogo(null);
    }

    const fetchProfileImage = async () => {
      // Skip if user is in registration flow (isRegistration flag is true)
      if (user?.isRegistration) {
        return;
      }

      if (role === "vendor" && user?._id) {
        try {
          try {
            //const res = await api.get("/vendor/profile");
            if (vendorProfile?.profileImageUrl) {
              setVendorLogo(vendorProfile.profileImageUrl);
              return;
            }
            if (vendorProfile?.storeLogo) {
              setVendorLogo(vendorProfile.storeLogo);
              return;
            }
            if (vendorProfile?.logo) {
              setVendorLogo(vendorProfile.logo);
              return;
            }
            if (vendorProfile?.avatar) {
              setVendorLogo(vendorProfile.avatar);
              return;
            }
          } catch (err) {
            console.error("Failed to fetch vendor profile", err);
          }

          try {
            const res = await api.get(`/vendor/${user._id}/store`);
            if (res.data?.vendor?.storeLogo) {
              setVendorLogo(res.data.vendor.storeLogo);
              return;
            }
            if (res.data?.vendor?.logo) {
              setVendorLogo(res.data.vendor.logo);
              return;
            }
          } catch (err) {
            console.error("Failed to fetch vendor store", err);
          }
        } catch (err) {
          console.error("Failed to fetch vendor logo", err);
        }
      }

      if (role === "agent") {
        try {
          //const res = await api.get("/agent/profile");
          const img = agentProfile?.profileImageUrl;
          if (img) {
            setVendorLogo(img);
          }
        } catch (err) {
          console.error("Failed to fetch agent profile", err);
        }
      }

      if (role === "customer") {
        try {
          //const res = await api.get("/customer/profile");
          if (user.profilePic) {
            setVendorLogo(user.profilePic);
          }
        } catch (err) {
          console.error("Failed to fetch customer profile", err);
        }
      }
    };
    fetchProfileImage();
  }, [role, user, vendorProfile, agentProfile]);

  // Debounced search suggestions
  useEffect(() => {
    const timer = setTimeout(() => {
      if (query.trim().length >= 2) {
        api
          .get(`/search/suggest?q=${query}`)
          .then((res) => {
            setSuggestions(res.data.products || []);
            setCategories(res.data.categories || []);
            setShowDropdown(true);
          })
          .catch((err) => {
            console.error("Search suggestion error", err);
            setSuggestions([]);
            setCategories([]);
          });
      } else {
        setSuggestions([]);
        setCategories([]);
        setShowDropdown(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  const handleClick = useCallback((e) => {
    if (!showDropdown) return;
    const isInsideDropdown = e.target.closest('[role="presentation"]');
    if (!isInsideDropdown) {
      setShowDropdown(false);
    }
  }, [showDropdown]);

  useEffect(() => {
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [handleClick]);

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    // setUser(null);
    logout();
    navigate("/login");
  };

  const getProfileRoute = (role) => {
    switch (role) {
      case "customer":
        return "/register/customer";
      case "vendor":
        return "/register/vendor";
      case "agent":
        return "/register/agent";
      case "admin":
        return "/admin";
      default:
        return "/login";
    }
  };

  return (
    <AppBar position="fixed" sx={{ bgcolor: "#131921", zIndex: 1300 }}>
      {/* ================= ROW 1 ================= */}
      <Toolbar
        sx={{
          minHeight: 64,
          display: "flex",
          gap: 2,
        }}
      >
        {/* LOGO */}
        <Typography
          component={Link}
          to="/"
          sx={{
            color: "#fff",
            textDecoration: "none",
            fontWeight: 700,
            fontSize: 20,
            whiteSpace: "nowrap",
          }}
        >
          <span style={{ color: "#aaa", marginRight: 6 }}>Pepper</span>Diamond
        </Typography>

        {/* 🔍 SEARCH (DESKTOP ONLY) */}
        <Box
          sx={{
            flex: 1,
            display: { xs: "none", md: "flex" },
            justifyContent: "center",
          }}
        >
          <SearchBox
            query={query}
            setQuery={setQuery}
            suggestions={suggestions}
            categories={categories}
            showDropdown={showDropdown}
            setShowDropdown={setShowDropdown}
          />
        </Box>

        {/* ICONS */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
          {!user?.isRegistration && (
            <HeaderIcons
              user={user}
              role={role}
              dashboardRoute={dashboardRoute}
              cartCount={cartCount}
            />
          )}
          <UserMenu
            user={user}
            role={role}
            avatarSrc={avatarSrc}
            anchorEl={anchorEl}
            menuOpen={menuOpen}
            setAnchorEl={setAnchorEl}
            setMenuOpen={setMenuOpen}
            handleLogout={handleLogout}
            getProfileRoute={getProfileRoute}
            navigate={navigate}
          />
        </Box>
      </Toolbar>

      {/* ================= ROW 2 (MOBILE/TABLET SEARCH) ================= */}
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          px: 2,
          pb: 1.5,
          justifyContent: "center",
        }}
      >
        <SearchBox
          query={query}
          setQuery={setQuery}
          suggestions={suggestions}
          categories={categories}
          showDropdown={showDropdown}
          setShowDropdown={setShowDropdown}
        />
      </Box>
    </AppBar>
  );
};

export default Header;
